// src/components/DocumentUpload.tsx
import React, { useState, useRef } from 'react';

interface DocumentUploadProps {
documentType: string;
  onUpload: (file: File, documentType: string) => void;
  onCancel: () => void;
  language?: string;
}

const DocumentUpload: React.FC<DocumentUploadProps> = ({
 documentType,
  onUpload,
  onCancel,
  language = 'en'
}) => {
  
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [preview, setPreview] = useState<string>('');
  const fileInputRef = useRef<HTMLInputElement>(null);
  
  // Translations
  const translations: Record<string, any> = {
    es: {
      upload: 'Subir',
      cancel: 'Cancelar',
      remove: 'Eliminar',
      clickToSelect: 'Haz clic para seleccionar',
      dragAndDrop: 'o arrastra y suelta tu documento aquí',
      supportedFormats: 'Formatos permitidos: JPG, PNG, HEIF (máx. 5MB)',
      supportedFormatsWithPdf: 'Formatos permitidos: JPG, PNG, HEIF, PDF (máx. 5MB)',
      pleaseUpload: 'Por favor sube tu',
      pleaseSelectFile: 'Por favor selecciona un archivo',
      fileSizeTooLarge: 'El archivo debe ser menor a 5MB',
      governmentDocError: 'Los documentos oficiales y selfies deben subirse como imágenes (JPG, PNG, HEIF). No se permiten PDFs.',
      utilityDocError: 'Las facturas y extractos bancarios pueden subirse como imágenes (JPG, PNG, HEIF) o PDFs.',
      invalidFileType: 'Por favor selecciona un archivo JPEG, PNG, HEIF o PDF',
      important: 'Importante',
      ensureVisible: 'Asegúrate de que el documento sea claramente visible',
      textReadable: 'Todo el texto debe ser legible',
      avoidGlare: 'Evita reflejos o sombras',
      selfieInstruction: 'Para selfies, sostén tu documento junto a tu cara',
      labels: {
        'passport': 'Pasaporte',
        'id_front': 'Documento de Identidad (Frente)',
        'id_back': 'Documento de Identidad (Reverso)',
        'license_front': 'Licencia de Conducir (Frente)',
        'license_back': 'Licencia de Conducir (Reverso)',
        'selfie': 'Selfie con Documento',
        'selfie_with_document': 'Selfie Sosteniendo tu Documento',
        'selfie_with_id': 'Selfie con Documento de Identidad',
        'utility_bill': 'Factura de Servicios',
        'bank_statement': 'Extracto Bancario'
      }
    },
    en: {
      upload: 'Upload',
      cancel: 'Cancel',
      remove: 'Remove',
      clickToSelect: 'Click to select',
      dragAndDrop: 'or drag & drop your document here',
      supportedFormats: 'Supported formats: JPG, PNG, HEIF (max 5MB)',
      supportedFormatsWithPdf: 'Supported formats: JPG, PNG, HEIF, PDF (max 5MB)',
      pleaseUpload: 'Please upload your',
      pleaseSelectFile: 'Please select a file',
      fileSizeTooLarge: 'File size must be less than 5MB',
      governmentDocError: 'Government documents and selfies must be uploaded as images (JPG, PNG, HEIF). PDFs are not allowed.',
      utilityDocError: 'Utility bills and bank statements can be uploaded as images (JPG, PNG, HEIF) or PDFs.',
      invalidFileType: 'Please select a JPEG, PNG, HEIF, or PDF file',
      important: 'Important',
      ensureVisible: 'Ensure the document is clearly visible',
      textReadable: 'All text should be readable',
      avoidGlare: 'Avoid glare or shadows',
      selfieInstruction: 'For selfies, hold your ID next to your face',
      labels: {
        'passport': 'Passport',
        'id_front': 'ID Card (Front)',
        'id_back': 'ID Card (Back)',
        'license_front': 'Driver License (Front)',
        'license_back': 'Driver License (Back)',
        'selfie': 'Selfie with ID',
        'selfie_with_document': 'Selfie Holding Your ID',
        'selfie_with_id': 'Selfie with ID Document',
        'utility_bill': 'Utility Bill',
        'bank_statement': 'Bank Statement'
      }
    },
    pt: {
      upload: 'Enviar',
      cancel: 'Cancelar',
      remove: 'Remover',
      clickToSelect: 'Clique para selecionar',
      dragAndDrop: 'ou arraste e solte seu documento aqui',
      supportedFormats: 'Formatos suportados: JPG, PNG, HEIF (máx. 5MB)',
      supportedFormatsWithPdf: 'Formatos suportados: JPG, PNG, HEIF, PDF (máx. 5MB)',
      pleaseUpload: 'Por favor, envie seu',
      pleaseSelectFile: 'Por favor, selecione um arquivo',
      fileSizeTooLarge: 'O arquivo deve ter menos de 5MB',
      governmentDocError: 'Documentos oficiais e selfies devem ser enviados como imagens (JPG, PNG, HEIF). PDFs não são permitidos.',
      utilityDocError: 'Contas de serviços e extratos bancários podem ser enviados como imagens (JPG, PNG, HEIF) ou PDFs.',
      invalidFileType: 'Por favor, selecione um arquivo JPEG, PNG, HEIF ou PDF',
      important: 'Importante',
      ensureVisible: 'Certifique-se de que o documento esteja claramente visível',
      textReadable: 'Todo o texto deve estar legível',
      avoidGlare: 'Evite reflexos ou sombras',
      selfieInstruction: 'Para selfies, segure seu documento ao lado do rosto',
      labels: {
        'passport': 'Passaporte',
        'id_front': 'Carteira de Identidade (Frente)',
        'id_back': 'Carteira de Identidade (Verso)',
        'license_front': 'Carteira de Motorista (Frente)',
        'license_back': 'Carteira de Motorista (Verso)',
        'selfie': 'Selfie com Documento',
        'selfie_with_document': 'Selfie Segurando seu Documento',
        'selfie_with_id': 'Selfie com Documento de Identidade',
        'utility_bill': 'Conta de Serviços',
        'bank_statement': 'Extrato Bancário'
      }
    },
    fr: {
      upload: 'Télécharger',
      cancel: 'Annuler',
      remove: 'Supprimer',
      clickToSelect: 'Cliquez pour sélectionner',
      dragAndDrop: 'ou glissez-déposez votre document ici',
      supportedFormats: 'Formats supportés: JPG, PNG, HEIF (max 5MB)',
      supportedFormatsWithPdf: 'Formats supportés: JPG, PNG, HEIF, PDF (max 5MB)',
      pleaseUpload: 'Veuillez télécharger votre',
      pleaseSelectFile: 'Veuillez sélectionner un fichier',
      fileSizeTooLarge: 'Le fichier doit être inférieur à 5MB',
      governmentDocError: 'Les documents officiels et selfies doivent être téléchargés en tant qu\'images (JPG, PNG, HEIF). Les PDFs ne sont pas autorisés.',
      utilityDocError: 'Les factures et relevés bancaires peuvent être téléchargés en tant qu\'images (JPG, PNG, HEIF) ou PDFs.',
      invalidFileType: 'Veuillez sélectionner un fichier JPEG, PNG, HEIF ou PDF',
      important: 'Important',
      ensureVisible: 'Assurez-vous que le document est clairement visible',
      textReadable: 'Tout le texte doit être lisible',
      avoidGlare: 'Évitez les reflets ou les ombres',
      selfieInstruction: 'Pour les selfies, tenez votre document à côté de votre visage',
      labels: {
        'passport': 'Passeport',
        'id_front': 'Carte d\'identité (Recto)',
        'id_back': 'Carte d\'identité (Verso)',
        'license_front': 'Permis de conduire (Recto)',
        'license_back': 'Permis de conduire (Verso)',
        'selfie': 'Selfie avec Document',
        'selfie_with_document': 'Selfie Tenant votre Document',
        'selfie_with_id': 'Selfie avec Pièce d\'identité',
        'utility_bill': 'Facture de Services',
        'bank_statement': 'Relevé Bancaire'
      }
    },
    de: {
      upload: 'Hochladen',
      cancel: 'Abbrechen',
      remove: 'Entfernen',
      clickToSelect: 'Klicken Sie zur Auswahl',
      dragAndDrop: 'oder ziehen Sie Ihr Dokument hierher',
      supportedFormats: 'Unterstützte Formate: JPG, PNG, HEIF (max. 5MB)',
      supportedFormatsWithPdf: 'Unterstützte Formate: JPG, PNG, HEIF, PDF (max. 5MB)',
      pleaseUpload: 'Bitte laden Sie Ihr',
      pleaseSelectFile: 'Bitte wählen Sie eine Datei',
      fileSizeTooLarge: 'Die Datei muss kleiner als 5MB sein',
      governmentDocError: 'Behördliche Dokumente und Selfies müssen als Bilder (JPG, PNG, HEIF) hochgeladen werden. PDFs sind nicht erlaubt.',
      utilityDocError: 'Rechnungen und Kontoauszüge können als Bilder (JPG, PNG, HEIF) oder PDFs hochgeladen werden.',
      invalidFileType: 'Bitte wählen Sie eine JPEG-, PNG-, HEIF- oder PDF-Datei',
      important: 'Wichtig',
      ensureVisible: 'Stellen Sie sicher, dass das Dokument deutlich sichtbar ist',
      textReadable: 'Der gesamte Text sollte lesbar sein',
      avoidGlare: 'Vermeiden Sie Blendung oder Schatten',
      selfieInstruction: 'Für Selfies halten Sie Ihr Dokument neben Ihr Gesicht',
      labels: {
        'passport': 'Reisepass',
        'id_front': 'Personalausweis (Vorderseite)',
        'id_back': 'Personalausweis (Rückseite)',
        'license_front': 'Führerschein (Vorderseite)',
        'license_back': 'Führerschein (Rückseite)',
        'selfie': 'Selfie mit Dokument',
        'selfie_with_document': 'Selfie mit Ihrem Dokument',
        'selfie_with_id': 'Selfie mit Ausweis',
        'utility_bill': 'Versorgungsrechnung',
        'bank_statement': 'Kontoauszug'
      }
    },
    it: {
      upload: 'Carica',
      cancel: 'Annulla',
      remove: 'Rimuovi',
      clickToSelect: 'Clicca per selezionare',
      dragAndDrop: 'o trascina il tuo documento qui',
      supportedFormats: 'Formati supportati: JPG, PNG, HEIF (max 5MB)',
      supportedFormatsWithPdf: 'Formati supportati: JPG, PNG, HEIF, PDF (max 5MB)',
      pleaseUpload: 'Per favore carica il tuo',
      pleaseSelectFile: 'Per favore seleziona un file',
      fileSizeTooLarge: 'Il file deve essere inferiore a 5MB',
      governmentDocError: 'I documenti ufficiali e i selfie devono essere caricati come immagini (JPG, PNG, HEIF). I PDF non sono ammessi.',
      utilityDocError: 'Le bollette e gli estratti bancari possono essere caricati come immagini (JPG, PNG, HEIF) o PDF.',
      invalidFileType: 'Per favore seleziona un file JPEG, PNG, HEIF o PDF',
      important: 'Importante',
      ensureVisible: 'Assicurati che il documento sia chiaramente visibile',
      textReadable: 'Tutto il testo deve essere leggibile',
      avoidGlare: 'Evita riflessi o ombre',
      selfieInstruction: 'Per i selfie, tieni il documento accanto al tuo viso',
      labels: {
        'passport': 'Passaporto',
        'id_front': 'Carta d\'identità (Fronte)',
        'id_back': 'Carta d\'identità (Retro)',
        'license_front': 'Patente di guida (Fronte)',
        'license_back': 'Patente di guida (Retro)',
        'selfie': 'Selfie con Documento',
        'selfie_with_document': 'Selfie con il tuo Documento',
        'selfie_with_id': 'Selfie con Documento d\'identità',
        'utility_bill': 'Bolletta',
        'bank_statement': 'Estratto Bancario'
      }
    },
    nl: {
      upload: 'Uploaden',
      cancel: 'Annuleren',
      remove: 'Verwijderen',
      clickToSelect: 'Klik om te selecteren',
      dragAndDrop: 'of sleep uw document hierheen',
      supportedFormats: 'Ondersteunde formaten: JPG, PNG, HEIF (max 5MB)',
      supportedFormatsWithPdf: 'Ondersteunde formaten: JPG, PNG, HEIF, PDF (max 5MB)',
      pleaseUpload: 'Upload alstublieft uw',
      pleaseSelectFile: 'Selecteer alstublieft een bestand',
      fileSizeTooLarge: 'Het bestand moet kleiner zijn dan 5MB',
      governmentDocError: 'Officiële documenten en selfies moeten als afbeeldingen (JPG, PNG, HEIF) worden geüpload. PDF\'s zijn niet toegestaan.',
      utilityDocError: 'Rekeningen en bankafschriften kunnen als afbeeldingen (JPG, PNG, HEIF) of PDF\'s worden geüpload.',
      invalidFileType: 'Selecteer alstublieft een JPEG-, PNG-, HEIF- of PDF-bestand',
      important: 'Belangrijk',
      ensureVisible: 'Zorg ervoor dat het document duidelijk zichtbaar is',
      textReadable: 'Alle tekst moet leesbaar zijn',
      avoidGlare: 'Vermijd schittering of schaduwen',
      selfieInstruction: 'Voor selfies, houd uw document naast uw gezicht',
      labels: {
        'passport': 'Paspoort',
        'id_front': 'Identiteitskaart (Voorkant)',
        'id_back': 'Identiteitskaart (Achterkant)',
        'license_front': 'Rijbewijs (Voorkant)',
        'license_back': 'Rijbewijs (Achterkant)',
        'selfie': 'Selfie met Document',
        'selfie_with_document': 'Selfie met uw Document',
        'selfie_with_id': 'Selfie met Identiteitsbewijs',
        'utility_bill': 'Nutsrekening',
        'bank_statement': 'Bankafschrift'
      }
    },
    ru: {
      upload: 'Загрузить',
      cancel: 'Отмена',
      remove: 'Удалить',
      clickToSelect: 'Нажмите для выбора',
      dragAndDrop: 'или перетащите документ сюда',
      supportedFormats: 'Поддерживаемые форматы: JPG, PNG, HEIF (макс. 5МБ)',
      supportedFormatsWithPdf: 'Поддерживаемые форматы: JPG, PNG, HEIF, PDF (макс. 5МБ)',
      pleaseUpload: 'Пожалуйста, загрузите ваш',
      pleaseSelectFile: 'Пожалуйста, выберите файл',
      fileSizeTooLarge: 'Файл должен быть меньше 5МБ',
      governmentDocError: 'Официальные документы и селфи должны быть загружены как изображения (JPG, PNG, HEIF). PDF не разрешены.',
      utilityDocError: 'Счета за коммунальные услуги и банковские выписки могут быть загружены как изображения (JPG, PNG, HEIF) или PDF.',
      invalidFileType: 'Пожалуйста, выберите файл JPEG, PNG, HEIF или PDF',
      important: 'Важно',
      ensureVisible: 'Убедитесь, что документ четко виден',
      textReadable: 'Весь текст должен быть читаемым',
      avoidGlare: 'Избегайте бликов или теней',
      selfieInstruction: 'Для селфи держите документ рядом с лицом',
      labels: {
        'passport': 'Паспорт',
        'id_front': 'Удостоверение личности (Лицевая)',
        'id_back': 'Удостоверение личности (Обратная)',
        'license_front': 'Водительские права (Лицевая)',
        'license_back': 'Водительские права (Обратная)',
        'selfie': 'Селфи с документом',
        'selfie_with_document': 'Селфи с вашим документом',
        'selfie_with_id': 'Селфи с удостоверением',
        'utility_bill': 'Счет за коммунальные услуги',
        'bank_statement': 'Банковская выписка'
      }
    },
    ar: {
      upload: 'تحميل',
      cancel: 'إلغاء',
      remove: 'حذف',
      clickToSelect: 'انقر للاختيار',
      dragAndDrop: 'أو اسحب وأفلت المستند هنا',
      supportedFormats: 'التنسيقات المدعومة: JPG، PNG، HEIF (حد أقصى 5 ميجابايت)',
      supportedFormatsWithPdf: 'التنسيقات المدعومة: JPG، PNG، HEIF، PDF (حد أقصى 5 ميجابايت)',
      pleaseUpload: 'يرجى تحميل',
      pleaseSelectFile: 'يرجى اختيار ملف',
      fileSizeTooLarge: 'يجب أن يكون حجم الملف أقل من 5 ميجابايت',
      governmentDocError: 'يجب تحميل المستندات الرسمية والصور الشخصية كصور (JPG، PNG، HEIF). ملفات PDF غير مسموح بها.',
      utilityDocError: 'يمكن تحميل فواتير الخدمات وكشوف الحساب البنكي كصور (JPG، PNG، HEIF) أو PDF.',
      invalidFileType: 'يرجى اختيار ملف JPEG أو PNG أو HEIF أو PDF',
      important: 'مهم',
      ensureVisible: 'تأكد من أن المستند مرئي بوضوح',
      textReadable: 'يجب أن يكون كل النص قابلاً للقراءة',
      avoidGlare: 'تجنب الوهج أو الظلال',
      selfieInstruction: 'للصور الشخصية، امسك المستند بجانب وجهك',
      labels: {
        'passport': 'جواز السفر',
        'id_front': 'بطاقة الهوية (الأمام)',
        'id_back': 'بطاقة الهوية (الخلف)',
        'license_front': 'رخصة القيادة (الأمام)',
        'license_back': 'رخصة القيادة (الخلف)',
        'selfie': 'صورة شخصية مع المستند',
        'selfie_with_document': 'صورة شخصية مع المستند الخاص بك',
        'selfie_with_id': 'صورة شخصية مع بطاقة الهوية',
        'utility_bill': 'فاتورة الخدمات',
        'bank_statement': 'كشف حساب بنكي'
      }
    }
  };
  
  const t = translations[language] || translations['en'];

  // Map document type to user-friendly label
  const getDocumentLabel = (type: string | null): string => {
    if (!type) return 'Document';
    return t.labels[type.toLowerCase()] || type;
  };

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Determine if this is a government document or selfie
    const governmentDocs = ['passport', 'id_front', 'id_back', 'license_front', 'license_back', 'selfie', 'selfie_with_document', 'selfie_with_id'];
    const utilityDocs = ['utility_bill', 'bank_statement', 'proof_of_address'];
    
    const isGovernmentDoc = governmentDocs.includes(documentType.toLowerCase());
    const isUtilityDoc = utilityDocs.includes(documentType.toLowerCase());
    
    // Validate file type based on document type
    const imageTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/heif', 'image/heic'];
    const pdfTypes = ['application/pdf'];
    
    if (isGovernmentDoc) {
      // Government docs and selfies: ONLY images allowed
      if (!imageTypes.includes(file.type.toLowerCase())) {
        alert(t.governmentDocError);
        return;
      }
    } else if (isUtilityDoc) {
      // Utility and bank docs: images OR PDFs allowed
      if (!imageTypes.includes(file.type.toLowerCase()) && !pdfTypes.includes(file.type.toLowerCase())) {
        alert(t.utilityDocError);
        return;
      }
    } else {
      // Default: allow both
      const validTypes = [...imageTypes, ...pdfTypes];
      if (!validTypes.includes(file.type.toLowerCase())) {
        alert(t.invalidFileType);
        return;
      }
    }

    // Validate file size (5MB max)
    if (file.size > 5 * 1024 * 1024) {
      alert(t.fileSizeTooLarge);
      return;
    }

    setSelectedFile(file);

    // Create preview for images
    if (file.type.startsWith('image/')) {
      const reader = new FileReader();
      reader.onloadend = () => {
        setPreview(reader.result as string);
      };
      reader.readAsDataURL(file);
    } else {
      setPreview('');
    }
  };

  const handleUpload = () => {
    if (!selectedFile) {
      alert(t.pleaseSelectFile);
      return;
    }

    // Use the documentType passed from parent, not selectedType
    onUpload(selectedFile, documentType);
  };

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    
    const file = e.dataTransfer.files[0];
    if (file) {
      const event = {
        target: { files: [file] }
      } as unknown as React.ChangeEvent<HTMLInputElement>;
      handleFileSelect(event);
    }
  };

  return (
    <div style={{
      position: 'fixed',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      backgroundColor: 'rgba(0, 0, 0, 0.5)',
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'center',
      zIndex: 1000,
    }}>
      <div style={{
        backgroundColor: 'white',
        borderRadius: '10px',
        padding: '30px',
        maxWidth: '500px',
        width: '90%',
        maxHeight: '80vh',
        overflowY: 'auto',
      }}>
        <h3 style={{ marginTop: 0 }}>{t.upload} {getDocumentLabel(documentType)}</h3>
        
        <p style={{ color: '#666', marginBottom: '20px' }}>
          {t.pleaseUpload} {getDocumentLabel(documentType).toLowerCase()}
        </p>

        <div
          onDragOver={handleDragOver}
          onDrop={handleDrop}
          onClick={() => fileInputRef.current?.click()}
          style={{
            border: '2px dashed #4CAF50',
            borderRadius: '10px',
            padding: '40px',
            textAlign: 'center',
            cursor: 'pointer',
            backgroundColor: selectedFile ? '#f0f8f0' : '#fafafa',
            marginBottom: '20px',
            display:"flex",
            flexWrap:"wrap",
            justifyContent:"center",
            alignItems:"center"
          }}
        >
          <input
            ref={fileInputRef}
            type="file"
            accept={
              // Only accept images for government docs/selfies
              ['passport', 'id_front', 'id_back', 'license_front', 'license_back', 'selfie', 'selfie_with_document', 'selfie_with_id'].includes(documentType.toLowerCase())
                ? "image/jpeg,image/jpg,image/png,image/heif,image/heic"
                : "image/jpeg,image/jpg,image/png,image/heif,image/heic,.pdf"
            }
            onChange={handleFileSelect}
            style={{ display: 'none' }}
          />
          
          {!selectedFile ? (
            <>
              <div style={{ fontSize: '48px', marginBottom: '10px' }}>📁</div>
              <p style={{ margin: '10px 0' }}>
                <strong>{t.clickToSelect}</strong> {t.dragAndDrop}
              </p>
              <p style={{ fontSize: '12px', color: '#666' }}>
                {['passport', 'id_front', 'id_back', 'license_front', 'license_back', 'selfie', 'selfie_with_document', 'selfie_with_id'].includes(documentType.toLowerCase()) ? t.supportedFormats : t.supportedFormatsWithPdf}
              </p>
            </>
          ) : (
            <>
              {preview ? (
                <img
                  src={preview}
                  alt="Document preview"
                  style={{
                    maxWidth: '100%',
                    // maxHeight: '200px',
                    marginBottom: '10px',
                    borderRadius: '5px',
                  }}
                />
              ) : (
                <div style={{ fontSize: '48px', marginBottom: '10px' }}>📄</div>
              )}
              <div>
              <p style={{ margin: '10px 0' }}>
                <strong>{selectedFile.name}</strong>
              </p>
              <p style={{ fontSize: '12px', color: '#666' }}>
                {(selectedFile.size / 1024).toFixed(2)} KB
              </p>
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  setSelectedFile(null);
                  setPreview('');
                }}
                style={{
                  marginTop: '10px',
                  padding: '5px 10px',
                  fontSize: '12px',
                  backgroundColor: '#f44336',
                  color: 'white',
                  border: 'none',
                  borderRadius: '3px',
                  cursor: 'pointer',
                }}
              >
                {t.remove}
              </button>
              </div>
            </>
          )}
        </div>

        <div style={{
          padding: '10px',
          backgroundColor: '#fff3cd',
          borderRadius: '5px',
          marginBottom: '20px',
          fontSize: '14px',
        }}>
          <strong>{t.important}:</strong>
          <ul style={{ margin: '5px 0', paddingLeft: '20px' }}>
            <li>{t.ensureVisible}</li>
            <li>{t.textReadable}</li>
            <li>{t.avoidGlare}</li>
            <li>{t.selfieInstruction}</li>
          </ul>
        </div>

        <div style={{
          display: 'flex',
          gap: '10px',
          justifyContent: 'flex-end',
        }}>
          <button
            onClick={onCancel}
            style={{
              padding: '10px 20px',
              fontSize: '14px',
              backgroundColor: '#f5f5f5',
              border: '1px solid #ddd',
              borderRadius: '5px',
              cursor: 'pointer',
            }}
          >
            {t.cancel}
          </button>
          <button
            onClick={handleUpload}
            disabled={!selectedFile}
            style={{
              padding: '10px 20px',
              fontSize: '14px',
              backgroundColor: selectedFile ? '#4CAF50' : '#ccc',
              color: 'white',
              border: 'none',
              borderRadius: '5px',
              cursor: selectedFile ? 'pointer' : 'not-allowed',
            }}
          >
            {t.upload}
          </button>
        </div>
      </div>
    </div>
  );
};

export default DocumentUpload;